/**
 * MINHA EQUIPE — página única com as quatro abas da rotina da equipe:
 * Painel · Turmas · Chamada · Mensalidades.
 *
 * Acesso: SOMENTE super_admin, professor, equipe (decisão do usuário) e o
 * MONITOR ATIVO — o aluno com vínculo em `teams.monitor_links`, que o backend
 * devolve em `/auth/me` como `sou_monitor` + `monitor` (papel continua
 * `athlete`). A federação e os demais papéis não entram. O backend recusa de
 * qualquer forma; aqui é só a porta da frente.
 *
 * A aba "Monitores" é de GESTÃO DE VÍNCULO: só super_admin, professor e equipe
 * a veem. O Monitor não vê a aba (nem o conteúdo) — ele não gerencia a si mesmo.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { CalendarCheck, ClipboardList, LayoutDashboard, UserCog, Wallet } from 'lucide-react';
import { minhaEquipeApi } from '../lib/api';
import PainelEquipe from '../components/minhaEquipe/PainelEquipe';
import TurmasEquipe from '../components/minhaEquipe/TurmasEquipe';
import ChamadaEquipe from '../components/minhaEquipe/ChamadaEquipe';
import MensalidadesEquipe from '../components/minhaEquipe/MensalidadesEquipe';
import MonitoresEquipe from '../components/minhaEquipe/MonitoresEquipe';

export default function MinhaEquipePage() {
  const { user, eMonitor, monitor } = useAuth();
  const navigate = useNavigate();
  const [aba, setAba] = useState('painel');
  const [turmas, setTurmas] = useState([]);
  const [carregando, setCarregando] = useState(true);

  // O Monitor ativo entra pela MESMA porta (ele é `athlete` no banco).
  const podeEntrar = ['super_admin', 'professor', 'team'].includes(user?.role) || !!eMonitor;
  // A aba de vínculos continua exclusiva de quem gere a equipe.
  const podeGerirMonitores = ['super_admin', 'professor', 'team'].includes(user?.role);

  const carregarTurmas = useCallback(async () => {
    try {
      const { data } = await minhaEquipeApi.turmas();
      setTurmas(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar as turmas');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!podeEntrar) { navigate('/dashboard'); return; }
    carregarTurmas();
  }, [podeEntrar, navigate, carregarTurmas]);

  if (!podeEntrar) return null;

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="minha-equipe-page">
        <CabecalhoPagina
          contexto="Cadastros"
          titulo="Minha Equipe"
          descricao="Turmas, chamada e mensalidades dos seus alunos — o mesmo aluno do e-Torneios, sem cadastro paralelo."
          icone={ClipboardList}
        />

        {/* MONITOR: aviso curto de escopo (reaproveita o estilo de alerta que a
            própria pasta já usa). Só aparece para o Monitor ativo. */}
        {eMonitor && (
          <div
            className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-sm text-amber-200"
            data-testid="aviso-monitor"
          >
            Você está usando o e-Torneios como Monitor da equipe {monitor?.team_name || 'da sua equipe'} —
            {' '}acesso somente às turmas do professor {monitor?.professor_name || 'que o promoveu'}.
          </div>
        )}

        <Tabs value={aba} onValueChange={setAba}>
          <TabsList className="bg-[#141414] border border-[#27272A]">
            <TabsTrigger value="painel" data-testid="aba-painel" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <LayoutDashboard size={16} className="mr-1" /> Painel
            </TabsTrigger>
            <TabsTrigger value="turmas" data-testid="aba-turmas" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <ClipboardList size={16} className="mr-1" /> Turmas
            </TabsTrigger>
            <TabsTrigger value="chamada" data-testid="aba-chamada" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <CalendarCheck size={16} className="mr-1" /> Chamada
            </TabsTrigger>
            <TabsTrigger value="mensalidades" data-testid="aba-mensalidades" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Wallet size={16} className="mr-1" /> Mensalidades
            </TabsTrigger>
            {podeGerirMonitores && (
              <TabsTrigger value="monitores" data-testid="aba-monitores" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                <UserCog size={16} className="mr-1" /> Monitores
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="painel" className="mt-4">
            <PainelEquipe onIrPara={setAba} />
          </TabsContent>
          <TabsContent value="turmas" className="mt-4">
            <TurmasEquipe turmas={turmas} onMudou={carregarTurmas} />
          </TabsContent>
          <TabsContent value="chamada" className="mt-4">
            {carregando ? null : <ChamadaEquipe turmas={turmas} />}
          </TabsContent>
          <TabsContent value="mensalidades" className="mt-4">
            <MensalidadesEquipe turmas={turmas} />
          </TabsContent>
          {podeGerirMonitores && (
            <TabsContent value="monitores" className="mt-4">
              <MonitoresEquipe />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </MainLayout>
  );
}
